import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {Inject} from 'inversify-props';

import {Connection} from '@/model/network/connection/Connection';
import {ConnectionType} from '@/model/network/connection/ConnectionType';

import {INetworkConnectionService} from '@/services/network/INetworkConnectionService';
import {Event} from '@/model/Event';
import {IEventsService} from '@/services/IEvents.service';
import {ConnectionState} from '@/model/network/connection/ConnectionState';

@Module({name: 'NetworkConnectionsStore', namespaced: true})
export default class NetworkConnectionsStore extends VuexModule {

  private static sort(connections: Connection[]): void {
    connections.sort((a, b) => a.Id.localeCompare(b.Id));
  }

  private connections: Connection[] | null = null;

  @Inject(Symbol.for('INetworkConnectionService'))
  private connectionService!: INetworkConnectionService;

  @Action({commit: 'setConnections', rawError: true})
  public async fetchConnections(fetchDetails: boolean = false) {
    const connections = await this.connectionService.readAll();

    if (fetchDetails) {
      for (const connection of connections) {
        Object.assign(connection, await this.connectionService.read(connection.Id));
      }
    }

    return connections;
  }

  @Mutation
  public setConnections(connections: Connection[]) {
    NetworkConnectionsStore.sort(connections);
    this.connections = connections;
  }

  public get Connections(): Connection[] | null {
    return this.connections;
  }

  public get ConnectionsVpn(): Connection[] | null {
    return this.connections?.filter((c) => c.Type === ConnectionType.Vpn) ?? null;
  }

  public get PrimaryConnection(): Connection | null {
    return this.connections?.find((c) => c.DevicesId?.includes('eth0')) ?? null;
  }

  @Action({rawError: true})
  public async fetchConnectionsDetails() {

    if (!this.connections) {
      await this.fetchConnections();
    }

    if (!this.connections) {
      throw new Error('Connection list still null after fetch');
    }

    for (const connection of this.connections) {
      await this.fetchConnection(connection.Id);
    }
  }

  @Action({commit: 'setConnection', rawError: true})
  public async fetchConnection(id: string) {
    return await this.connectionService.read(id);
  }

  @Mutation
  public setConnection(connection: Connection) {

    if (!this.connections) {
      this.connections = [];
    }

    this.connections = this.connections.filter((p) => p.Id !== connection.Id);
    this.connections.push(connection);
    NetworkConnectionsStore.sort(this.connections);
  }

  @Mutation
  public removeConnection(connection: Connection) {

    if (!this.connections) {
      throw new Error('connection could not be removed: no existing connections');
    }

    const foundConnection = this.connections.find((c) => c.UUID === connection.UUID);
    if (!foundConnection) {
      throw new Error('connection could not be removed: not found');
    }

    this.connections = this.connections.filter((c) => c !== foundConnection);
  }

  @Mutation
  public setState(args: any) {
    args.connection.State = args.state;
  }
}
